import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import {
  ArrowLeft,
  History,
  Loader2,
  Printer,
  Search as SearchIcon,
  RefreshCw,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtDateTime } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { useAssets, useLookups, type AssetListFilters, type AssetListRow } from "@/features/assets/api";
import { ASSET_CLASSES, ASSET_STATUSES, ASSET_TYPES, ALLOCATION_STATUSES } from "@/features/assets/types";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canPrintQrLabels } from "@/lib/permissions";
import { useBranch } from "@/context/BranchContext";
import { isBranchLocked, isDeptScopedOnly } from "@/lib/roles";
import { ActionGuard } from "@/components/auth/ActionGuard";
import { PermissionDenied } from "@/components/auth/PermissionDenied";

import {
  PrintSheet,
  TEMPLATE_LABELS,
  PAPER_LABELS,
  totalPages,
  labelsPerPage,
  type Columns,
  type LabelTemplate,
  type PaperSize,
  type QrSize,
} from "@/features/assets/qr-bulk/PrintSheet";
import {
  useRecordPrintBatch,
  useQrPrintBatches,
  useQrBatchAssets,
  useAssetsByIds,
} from "@/features/assets/qr-bulk/api";
import { isBuildingAsset, isVehicleAsset } from "@/features/assets/classification";

const searchSchema = z.object({
  selected: z.string().optional(),
  batch: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/assets/qr-labels")({
  validateSearch: (s: Record<string, unknown>) => searchSchema.parse(s),
  component: QrLabelsPage,
});

type Scope =
  | "selected"
  | "branch"
  | "department"
  | "building"
  | "floor"
  | "location"
  | "asset_class"
  | "asset_type"
  | "asset_category"
  | "newly_added"
  | "without_qr"
  | "custom";

const SCOPE_LABELS: Record<Scope, string> = {
  selected: "Selected Assets",
  branch: "Branch",
  department: "Department",
  building: "Building",
  floor: "Floor",
  location: "Room / Location",
  asset_class: "Asset Class",
  asset_type: "Asset Type",
  asset_category: "Asset Category",
  newly_added: "Newly Added (last 30 days)",
  without_qr: "Assets Without QR Printed",
  custom: "Custom Filter",
};

const ALL = "__all__";

type QrPrinted = "all" | "printed" | "not_printed";

type LocalFilters = {
  scope: Scope;
  branchId: string | null;
  departmentId: string | null;
  building: string | null; // building_name
  floor: string | null;
  locationId: string | null;
  assetClass: string | null;
  assetType: string | null;
  categoryId: string | null;
  status: string | null;
  allocationStatus: string | null;
  qrPrinted: QrPrinted;
  purchaseFrom: string;
  purchaseTo: string;
  createdFrom: string;
  createdTo: string;
  search: string;
};

const emptyFilters = (branchId: string | null): LocalFilters => ({
  scope: "custom",
  branchId,
  departmentId: null,
  building: null,
  floor: null,
  locationId: null,
  assetClass: null,
  assetType: null,
  categoryId: null,
  status: null,
  allocationStatus: null,
  qrPrinted: "all",
  purchaseFrom: "",
  purchaseTo: "",
  createdFrom: "",
  createdTo: "",
  search: "",
});

function QrLabelsPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/assets/qr-labels" });
  const { data: me } = useCurrentUser();
  const roles = me?.roles ?? [];
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = isBranchLocked(roles)
    ? (me?.profile?.branch_id ?? null)
    : (lockedBranchId ?? currentBranchId ?? null);

  const [filters, setFilters] = useState<LocalFilters>(() =>
    emptyFilters(effectiveBranchId),
  );
  
  const [activeTab, setActiveTab] = useState(search.batch ? "history" : "print");

  // Handoff from Asset Registry with checked IDs.
  const preselectIds = useMemo(() => {
    if (!search.selected) return [] as string[];
    return search.selected.split(",").filter(Boolean);
  }, [search.selected]);

  useEffect(() => {
    if (preselectIds.length > 0) {
      setFilters((f) => ({ ...f, scope: "selected" }));
    }
  }, [preselectIds.length]);

  // Sync branch context if the user has a branch lock/switch.
  useEffect(() => {
    setFilters((f) => (f.branchId === effectiveBranchId ? f : { ...f, branchId: effectiveBranchId }));
  }, [effectiveBranchId]);

  if (!canPrintQrLabels(roles)) {
    return <PermissionDenied />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-1 -ml-2">
            <Link to="/assets">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to Asset Registry
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">Print QR Labels</h1>
          <p className="text-sm text-muted-foreground">
            Choose a print scope, refine assets, pick a template, then print.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="print">
            <Printer className="mr-1 h-4 w-4" /> New Print Batch
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="mr-1 h-4 w-4" /> History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="print">
          <NewBatchTab
            filters={filters}
            setFilters={setFilters}
            preselectIds={preselectIds}
            roles={roles}
            effectiveBranchId={effectiveBranchId}
          />
        </TabsContent>
        <TabsContent value="history">
          <HistoryTab
            focusBatchId={search.batch ?? null}
            onReprint={(assetIds, template) => {
              // Jump back to Print tab with these IDs preselected via query.
              navigate({
                to: "/assets/qr-labels",
                search: { selected: assetIds.join(",") },
              });
              setFilters((f) => ({ ...f, scope: "selected" }));
              setActiveTab("print");
              // template will be picked up by user in Step C.
              void template;
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- New batch (scope → filters → preview → template → print) ----------

function NewBatchTab({
  filters,
  setFilters,
  preselectIds,
  roles,
  effectiveBranchId,
}: {
  filters: LocalFilters;
  setFilters: React.Dispatch<React.SetStateAction<LocalFilters>>;
  preselectIds: string[];
  roles: import("@/lib/roles").AppRole[];
  effectiveBranchId: string | null;
}) {
  void roles;
  const { data: lookups } = useLookups();

  // Building / floor pickers pull distinct values from asset table via query
  // in the assets list result. For MVP we take building names from locations
  // (kind='building') and floor from a free text input.
  const buildings = useMemo(
    () => (lookups?.locations ?? []).filter((l) => l.kind === "building"),
    [lookups],
  );
  const rooms = useMemo(
    () => (lookups?.locations ?? []).filter((l) => l.kind === "room"),
    [lookups],
  );
  const depts = useMemo(
    () =>
      (lookups?.departments ?? []).filter(
        (d) => !filters.branchId || d.branch_id === filters.branchId,
      ),
    [lookups, filters.branchId],
  );

  // Translate LocalFilters + Scope into an AssetListFilters query.
  const listFilters: AssetListFilters = useMemo(() => {
    const f: AssetListFilters = {
      page: 1,
      pageSize: 500,
      branchId: filters.branchId,
    };
    if (filters.scope === "selected") return f; // ids-based, not filter based
    if (filters.scope === "department" && filters.departmentId) f.departmentId = filters.departmentId;
    if (filters.scope === "asset_class" && filters.assetClass) f.assetClass = filters.assetClass;
    if (filters.scope === "asset_type" && filters.assetType) f.assetType = filters.assetType;
    if (filters.scope === "asset_category" && filters.categoryId) f.categoryId = filters.categoryId;
    if (filters.scope === "location" && filters.locationId) {
      // Not a first-class filter on useAssets — fallback client filter below.
    }
    // Common filters always applied
    if (filters.departmentId && filters.scope !== "department") f.departmentId = filters.departmentId;
    if (filters.categoryId && filters.scope !== "asset_category") f.categoryId = filters.categoryId;
    if (filters.assetClass && filters.scope !== "asset_class") f.assetClass = filters.assetClass;
    if (filters.assetType && filters.scope !== "asset_type") f.assetType = filters.assetType;
    if (filters.status) f.status = filters.status;
    if (filters.allocationStatus) f.allocationStatus = filters.allocationStatus;
    if (filters.search.trim()) f.search = filters.search.trim();
    return f;
  }, [filters]);

  // Feed by IDs when scope=selected, otherwise via filter query.
  const idsQuery = useAssetsByIds(filters.scope === "selected" ? preselectIds : []);
  const filterQuery = useAssets(filters.scope === "selected" ? { pageSize: 0, page: 1 } : listFilters);

  const rawRows: AssetListRow[] = useMemo(() => {
    if (filters.scope === "selected") return idsQuery.data ?? [];
    return filterQuery.data?.rows ?? [];
  }, [filters.scope, idsQuery.data, filterQuery.data]);

  // Client-side post filters (building/floor/location/qr-printed/dates/newly).
  const rows = useMemo(() => {
    const now = Date.now();
    return rawRows.filter((a) => {
      if (filters.scope === "newly_added") {
        const created = a.created_at ? new Date(a.created_at).getTime() : 0;
        if (now - created > 30 * 24 * 3600 * 1000) return false;
      }
      if (filters.scope === "without_qr") {
        const count = (a as { qr_print_count?: number | null }).qr_print_count ?? 0;
        if (count > 0 || a.qr_printed_at) return false;
      }
      if (filters.qrPrinted === "printed") {
        const count = (a as { qr_print_count?: number | null }).qr_print_count ?? 0;
        if (count === 0 && !a.qr_printed_at) return false;
      }
      if (filters.qrPrinted === "not_printed") {
        const count = (a as { qr_print_count?: number | null }).qr_print_count ?? 0;
        if (count > 0 || a.qr_printed_at) return false;
      }
      if (filters.building) {
        const bn = (a.building_name ?? "").toLowerCase();
        const target = filters.building.toLowerCase();
        if (!bn.includes(target)) return false;
      }
      if (filters.floor) {
        const floor = String(a.number_of_floors ?? "");
        if (!floor.includes(filters.floor)) return false;
      }
      if (filters.locationId && a.location?.id !== filters.locationId) return false;
      if (filters.purchaseFrom) {
        const inv = a.invoice_date ? new Date(a.invoice_date).getTime() : 0;
        if (inv < new Date(filters.purchaseFrom).getTime()) return false;
      }
      if (filters.purchaseTo) {
        const inv = a.invoice_date ? new Date(a.invoice_date).getTime() : 0;
        if (inv > new Date(filters.purchaseTo).getTime()) return false;
      }
      if (filters.createdFrom) {
        const c = a.created_at ? new Date(a.created_at).getTime() : 0;
        if (c < new Date(filters.createdFrom).getTime()) return false;
      }
      if (filters.createdTo) {
        const c = a.created_at ? new Date(a.created_at).getTime() : 0;
        if (c > new Date(filters.createdTo).getTime()) return false;
      }
      return true;
    });
  }, [rawRows, filters]);

  const loading = filters.scope === "selected" ? idsQuery.isLoading : filterQuery.isLoading;

  // ----- Preview selection -----
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [previewSearch, setPreviewSearch] = useState("");

  // Auto-check everything the first time rows land.
  useEffect(() => {
    setChecked(new Set(rows.map((r) => r.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.map((r) => r.id).join("|")]);

  const previewRows = useMemo(() => {
    const q = previewSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.asset_code.toLowerCase().includes(q) || (r.name ?? "").toLowerCase().includes(q),
    );
  }, [rows, previewSearch]);

  const selectedRows = useMemo(() => rows.filter((r) => checked.has(r.id)), [rows, checked]);

  const allChecked = previewRows.length > 0 && previewRows.every((r) => checked.has(r.id));
  const toggleAll = () => {
    const next = new Set(checked);
    if (allChecked) previewRows.forEach((r) => next.delete(r.id));
    else previewRows.forEach((r) => next.add(r.id));
    setChecked(next);
  };
  const toggleOne = (id: string) => {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChecked(next);
  };
  const removeFromBatch = (id: string) => {
    const next = new Set(checked);
    next.delete(id);
    setChecked(next);
  };

  // ----- Template / sheet -----
  const [template, setTemplate] = useState<LabelTemplate>("standard");
  const [columns, setColumns] = useState<Columns>(3);
  const [qrSize, setQrSize] = useState<QrSize>("md");
  const [paperSize, setPaperSize] = useState<PaperSize>("A4");


  // Auto-suggest template when all selected are vehicles or buildings.
  useEffect(() => {
    if (selectedRows.length === 0) return;
    if (selectedRows.every(isBuildingAsset)) setTemplate("building");
    else if (selectedRows.every(isVehicleAsset)) setTemplate("vehicle");
  }, [selectedRows]);

  // ----- Print + log -----
  const recordMut = useRecordPrintBatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastPrintedIds, setLastPrintedIds] = useState<string[] | null>(null);

  const doPrint = () => {
    if (selectedRows.length === 0) {
      toast.error("Select at least one asset");
      return;
    }
    setLastPrintedIds(selectedRows.map((r) => r.id));
    // Give the DOM a tick so the sheet CSS reflows before print.
    setTimeout(() => {
      window.print();
      setConfirmOpen(true);
    }, 50);
  };

  const markPrinted = async (ok: boolean) => {
    setConfirmOpen(false);
    if (!ok || !lastPrintedIds || lastPrintedIds.length === 0) return;
    try {
      await recordMut.mutateAsync({
        assetIds: lastPrintedIds,
        template,
        scope: filters.scope,
        scopeMeta: { ...buildScopeMeta(filters), paperSize, columns, qrSize },
        branchId: filters.branchId ?? effectiveBranchId,
      });
      toast.success(`Logged ${lastPrintedIds.length} label${lastPrintedIds.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error((e as Error).message || "Could not save print log");
    }
  };

  return (
    <div className="space-y-4">
      {/* Step A — Scope + filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Scope &amp; Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div>
              <Label className="text-xs">Print Scope</Label>
              <Select
                value={filters.scope}
                onValueChange={(v) => setFilters((f) => ({ ...f, scope: v as Scope }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(SCOPE_LABELS) as Scope[]).map((k) => (
                    <SelectItem
                      key={k}
                      value={k}
                      disabled={k === "selected" && preselectIds.length === 0}
                    >
                      {SCOPE_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <FilterSelect
              label="Branch"
              value={filters.branchId}
              onChange={(v) => setFilters((f) => ({ ...f, branchId: v }))}
              options={(lookups?.branches ?? []).map((b) => ({ value: b.id, label: b.name }))}
              disabled={isBranchLocked((useCurrentUser().data?.roles ?? []))}
            />
            <FilterSelect
              label="Department"
              value={filters.departmentId}
              onChange={(v) => setFilters((f) => ({ ...f, departmentId: v }))}
              options={depts.map((d) => ({ value: d.id, label: d.name }))}
            />
            <FilterSelect
              label="Category"
              value={filters.categoryId}
              onChange={(v) => setFilters((f) => ({ ...f, categoryId: v }))}
              options={(lookups?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <FilterSelect
              label="Building (by location)"
              value={filters.building}
              onChange={(v) => setFilters((f) => ({ ...f, building: v }))}
              options={buildings.map((b) => ({ value: b.name, label: b.name }))}
            />
            <div>
              <Label className="text-xs">Floor</Label>
              <Input
                value={filters.floor ?? ""}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, floor: e.target.value || null }))
                }
                placeholder="e.g. 2"
              />
            </div>
            <FilterSelect
              label="Room / Location"
              value={filters.locationId}
              onChange={(v) => setFilters((f) => ({ ...f, locationId: v }))}
              options={rooms.map((r) => ({ value: r.id, label: r.name }))}
            />
            <FilterSelect
              label="Asset Class"
              value={filters.assetClass}
              onChange={(v) => setFilters((f) => ({ ...f, assetClass: v }))}
              options={ASSET_CLASSES.map((c) => ({ value: c, label: c }))}
            />
            <FilterSelect
              label="Asset Type"
              value={filters.assetType}
              onChange={(v) => setFilters((f) => ({ ...f, assetType: v }))}
              options={ASSET_TYPES.map((t) => ({ value: t, label: t }))}
            />
            <FilterSelect
              label="Asset Status"
              value={filters.status}
              onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
              options={ASSET_STATUSES.map((s) => ({ value: s, label: s }))}
            />
            <FilterSelect
              label="Allocation Status"
              value={filters.allocationStatus}
              onChange={(v) => setFilters((f) => ({ ...f, allocationStatus: v }))}
              options={ALLOCATION_STATUSES.map((s) => ({ value: s, label: s }))}
            />
            <FilterSelect
              label="QR Printed"
              value={filters.qrPrinted === "all" ? null : filters.qrPrinted}
              onChange={(v) =>
                setFilters((f) => ({ ...f, qrPrinted: (v as QrPrinted) ?? "all" }))
              }
              options={[
                { value: "printed", label: "Printed" },
                { value: "not_printed", label: "Not Printed" },
              ]}
              allLabel="All"
            />
            <div>
              <Label className="text-xs">Purchase From</Label>
              <Input
                type="date"
                value={filters.purchaseFrom}
                onChange={(e) => setFilters((f) => ({ ...f, purchaseFrom: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Purchase To</Label>
              <Input
                type="date"
                value={filters.purchaseTo}
                onChange={(e) => setFilters((f) => ({ ...f, purchaseTo: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Created From</Label>
              <Input
                type="date"
                value={filters.createdFrom}
                onChange={(e) => setFilters((f) => ({ ...f, createdFrom: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Created To</Label>
              <Input
                type="date"
                value={filters.createdTo}
                onChange={(e) => setFilters((f) => ({ ...f, createdTo: e.target.value }))}
              />
            </div>
          </div>
          <div className="relative max-w-md">
            <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search asset code or name…"
              className="pl-8"
            />
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFilters(emptyFilters(effectiveBranchId))}
            >
              <RefreshCw className="mr-1 h-3 w-3" /> Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step B — Preview */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">2. Preview</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative w-56">
              <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={previewSearch}
                onChange={(e) => setPreviewSearch(e.target.value)}
                placeholder="Filter within preview…"
                className="pl-8"
              />
            </div>
            <Badge variant="secondary">
              {selectedRows.length} of {rows.length} selected
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={allChecked} onCheckedChange={toggleAll} />
                </TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Class</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Building / Floor / Room</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>QR Printed</TableHead>
                <TableHead>Last Printed</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} className="py-10 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : previewRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="py-10 text-center text-muted-foreground">
                    No assets match the current scope &amp; filters.
                  </TableCell>
                </TableRow>
              ) : (
                previewRows.map((a) => {
                  const printedCount = (a as { qr_print_count?: number | null }).qr_print_count ?? 0;
                  const printed = printedCount > 0 || !!a.qr_printed_at;
                  return (
                    <TableRow key={a.id}>
                      <TableCell>
                        <Checkbox
                          checked={checked.has(a.id)}
                          onCheckedChange={() => toggleOne(a.id)}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{a.asset_code}</TableCell>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell><Badge variant="secondary">{a.asset_class ?? "Equipment"}</Badge></TableCell>
                      <TableCell>{a.asset_type ?? "—"}</TableCell>
                      <TableCell>{a.department?.name ?? "—"}</TableCell>
                      <TableCell className="text-xs">
                        {[a.building_name, a.number_of_floors, a.location?.name]
                          .filter(Boolean)
                          .join(" / ") || "—"}
                      </TableCell>
                      <TableCell>{a.status}</TableCell>
                      <TableCell>
                        {printed ? (
                          <Badge>Yes</Badge>
                        ) : (
                          <Badge variant="outline">No</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {a.qr_printed_at ? fmtDateTime(a.qr_printed_at) : "—"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFromBatch(a.id)}
                          title="Remove from batch"
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Step C — Template + Print */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">3. Template &amp; Print</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to filters
            </Button>
            <ActionGuard can={selectedRows.length > 0}>
              <Button size="sm" onClick={doPrint}>
                <Printer className="mr-1 h-4 w-4" /> Print {selectedRows.length} label
                {selectedRows.length === 1 ? "" : "s"}
              </Button>
            </ActionGuard>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div>
              <Label className="text-xs">Template</Label>
              <Select value={template} onValueChange={(v) => setTemplate(v as LabelTemplate)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(TEMPLATE_LABELS) as LabelTemplate[]).map((k) => (
                    <SelectItem key={k} value={k}>{TEMPLATE_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Paper</Label>
              <Select value={paperSize} onValueChange={(v) => setPaperSize(v as PaperSize)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(PAPER_LABELS) as PaperSize[]).map((k) => (
                    <SelectItem key={k} value={k}>{PAPER_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Columns</Label>
              <Select value={String(columns)} onValueChange={(v) => setColumns(Number(v) as Columns)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                  <SelectItem value="4">4</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">QR size</Label>
              <Select value={qrSize} onValueChange={(v) => setQrSize(v as QrSize)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sm">Small</SelectItem>
                  <SelectItem value="md">Medium</SelectItem>
                  <SelectItem value="lg">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-2">
            <Badge variant="outline" className="font-normal bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
              {labelsPerPage(columns, qrSize, paperSize)} labels fit per {paperSize} sheet
            </Badge>
            <span>·</span>
            <span>
              {selectedRows.length} total label{selectedRows.length === 1 ? "" : "s"} to print
            </span>
            <span>·</span>
            <span>
              Requires {totalPages(selectedRows.length, columns, qrSize, paperSize)} {paperSize} sheet{totalPages(selectedRows.length, columns, qrSize, paperSize) === 1 ? "" : "s"}
            </span>
          </div>

          <div className="rounded border bg-neutral-100 p-3">
            {selectedRows.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Select assets in step 2 to preview labels.
              </div>
            ) : (
              <PrintSheet
                assets={selectedRows}
                template={template}
                columns={columns}
                qrSize={qrSize}
                paperSize={paperSize}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Were labels printed successfully?</AlertDialogTitle>
            <AlertDialogDescription>
              Confirming will log this batch and mark {lastPrintedIds?.length ?? 0} asset
              {(lastPrintedIds?.length ?? 0) === 1 ? "" : "s"} as printed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => markPrinted(false)}>No, skip</AlertDialogCancel>
            <AlertDialogAction onClick={() => markPrinted(true)}>Yes, mark as printed</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------- History tab ----------

function HistoryTab({
  focusBatchId,
  onReprint,
}: {
  focusBatchId: string | null;
  onReprint: (assetIds: string[], template: LabelTemplate) => void;
}) {
  const { data: batches, isLoading } = useQrPrintBatches();
  const [openBatch, setOpenBatch] = useState<string | null>(focusBatchId);

  useEffect(() => {
    setOpenBatch(focusBatchId);
  }, [focusBatchId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Print Batch History</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Batch</TableHead>
              <TableHead>Printed At</TableHead>
              <TableHead>Printed By</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead className="text-right">Labels</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : (batches ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                  No print batches yet.
                </TableCell>
              </TableRow>
            ) : (
              (batches ?? []).map((b) => (
                <TableRow key={b.id}>
                  <TableCell className="font-mono text-xs">{b.id.slice(0, 8)}</TableCell>
                  <TableCell>{fmtDateTime(b.created_at)}</TableCell>
                  <TableCell>{b.printed_by?.full_name ?? b.printed_by?.email ?? "—"}</TableCell>
                  <TableCell>{SCOPE_LABELS[b.scope as Scope] ?? b.scope}</TableCell>
                  <TableCell>{b.template}</TableCell>
                  <TableCell>{b.branch?.name ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{b.label_count}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setOpenBatch(b.id)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {openBatch && (
          <BatchAssetsDialog
            batchId={openBatch}
            onClose={() => setOpenBatch(null)}
            onReprint={(ids) => {
              const b = (batches ?? []).find((x) => x.id === openBatch);
              onReprint(ids, (b?.template as LabelTemplate) ?? "standard");
              setOpenBatch(null);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function BatchAssetsDialog({
  batchId,
  onClose,
  onReprint,
}: {
  batchId: string;
  onClose: () => void;
  onReprint: (assetIds: string[]) => void;
}) {
  const { data: logs } = useQrBatchAssets(batchId);
  const ids = useMemo(() => (logs ?? []).map((l) => l.asset_id), [logs]);
  const { data: assets } = useAssetsByIds(ids);

  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Batch assets</AlertDialogTitle>
          <AlertDialogDescription>
            {(assets ?? []).length} asset{(assets ?? []).length === 1 ? "" : "s"} in this batch.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="max-h-80 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Department</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(assets ?? []).map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono text-xs">{a.asset_code}</TableCell>
                  <TableCell>{a.name}</TableCell>
                  <TableCell>{a.department?.name ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          <AlertDialogAction onClick={() => onReprint(ids)}>Reprint batch</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------- helpers ----------

function FilterSelect({
  label,
  value,
  onChange,
  options,
  disabled,
  allLabel = "All",
}: {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  allLabel?: string;
}) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select
        value={value ?? ALL}
        onValueChange={(v) => onChange(v === ALL ? null : v)}
        disabled={disabled}
      >
        <SelectTrigger><SelectValue placeholder={allLabel} /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function buildScopeMeta(f: LocalFilters): Record<string, unknown> {
  const meta: Record<string, unknown> = {};
  if (f.branchId) meta.branchId = f.branchId;
  if (f.departmentId) meta.departmentId = f.departmentId;
  if (f.categoryId) meta.categoryId = f.categoryId;
  if (f.building) meta.building = f.building;
  if (f.floor) meta.floor = f.floor;
  if (f.locationId) meta.locationId = f.locationId;
  if (f.assetClass) meta.assetClass = f.assetClass;
  if (f.assetType) meta.assetType = f.assetType;
  if (f.status) meta.status = f.status;
  if (f.allocationStatus) meta.allocationStatus = f.allocationStatus;
  return meta;
}
